<script setup lang="ts">
import { ImageOptions, PermissionOrganization, UploadViewDataQuery } from '@/generated/graphql'
import UploadPreview, { PreviewableUpload } from '@/components/upload/UploadPreview.vue'
import UploadUsageLocationsDialog from '@/components/upload/UploadUsageLocationsDialog.vue'
import DateTime from '@/components/DateTime.vue'
import ButtonDelete from '@/components/button/ButtonDelete.vue'
import ButtonEdit from '@/components/button/ButtonEdit.vue'
import useAuthorization from '@/composables/useAuthorization'
import { ComponentExposed } from 'vue-component-type-helpers'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

type UploadItem = UploadViewDataQuery['upload']['search'][0]

const props = defineProps<{
  upload: UploadItem
  imageOptions: ImageOptions
}>()

const emit = defineEmits<{
  edit: [upload: typeof props.upload]
  delete: [upload: typeof props.upload]
}>()

const { reactiveOrgAuthz } = useAuthorization()
const { t } = useI18n()

const usageLocationsDialog = ref<ComponentExposed<typeof UploadUsageLocationsDialog>>()

const showUsageLocations = async () => {
  await usageLocationsDialog.value?.open()
}
</script>

<template>
  <v-card density="compact" style="height: 100%" class="upload-card">
    <v-card-title style="height: 220px">
      <upload-preview
        :upload="upload as unknown as PreviewableUpload"
        :height="imageOptions.height"
        :width="imageOptions.width"
        icon-size="100"
      />
    </v-card-title>
    <v-card-text class="upload-card-content">
      <div class="text-body-2 font-weight-bold upload-name mb-1">{{ upload.name }}</div>
      <div class="text-caption">{{ upload.contentType }}</div>
      <div class="text-caption">
        <date-time :model-value="upload.createdAt" />
      </div>
    </v-card-text>
    <v-card-actions class="upload-card-actions">
      <v-btn
        v-if="upload.usageLocations.length > 0"
        color="primary"
        variant="flat"
        density="default"
        @click="showUsageLocations"
      >
        {{ t('button.viewUsages') }}
        <v-chip density="compact" size="x-small" class="ml-1" color="white" text-color="primary">
          {{ upload.usageLocations.length }}
        </v-chip>
      </v-btn>
      <v-spacer />
      <div class="d-flex">
        <button-edit
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgUploadWrite).value"
          @click="emit('edit', upload)"
        />
        <button-delete
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgUploadDelete).value"
          :disable-reason="
            upload.usageLocations.length > 0
              ? t('component.uploadCard.disableDeleteBecauseUsed', {
                  count: upload.usageLocations.length,
                })
              : undefined
          "
          @click="emit('delete', upload)"
        />
      </div>
    </v-card-actions>
    <upload-usage-locations-dialog
      ref="usageLocationsDialog"
      :usage-locations="upload.usageLocations"
      :upload-id="upload.id"
    />
  </v-card>
</template>

<style scoped lang="scss">
.upload-card {
  display: flex;
  flex-direction: column;

  .upload-card-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 0 16px;
  }

  .upload-card-actions {
    flex-shrink: 0;
    padding: 0 8px 8px 16px;
    margin-top: auto;
  }
}

.upload-name {
  word-break: break-word;
  overflow-wrap: break-word;
}
</style>
